import { memo, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";

import Fuse from "fuse.js";
import { flatMap, intersection, isEqual, map, uniq } from "lodash";

import { Search } from "@mui/icons-material";
import {
  Box,
  Chip,
  FormControl,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  SelectProps,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { useAppConfigQuery } from "#/features/User/queries";
import { IAppConfig } from "#/features/User/types";
import { IUnit } from "@/api";
import { useDeepMemo, useKeyBy } from "@/common/hooks";

import { CompactFloatedTag } from "../CompactFloatedTag/CompactFloatedTag";
import { Checkbox } from "../TrackedComponents";

import { useHandleQueryParams } from "./hooks";
import "./UnitPicker.scss";

// Types for hierarchy
interface IHierarchyNode {
  id: string;
  name: string;
  parentId?: string;
  type: "market" | "facility" | "unit";
  level: number;
  unitId?: string; // For unit nodes, store the actual unit ID
}

// Type for node selection state
interface INodeSelectionState {
  allSelected: boolean;
  someSelected: boolean;
  unitIds: string[];
}

const sectionNames: Record<IHierarchyNode["type"], string> = {
  market: "FACILITIES",
  facility: "UNITS",
  unit: "",
} as const;

/**
 * UnitPicker component - Allows selecting units with grouping based on Market -> Facility -> Unit hierarchy
 * This component is used for desktop devices.
 *
 * Rendering updated to use Tooltip / Popper.
 * https://v5.mui.com/material-ui/react-tooltip/#basic-tooltip
 * https://v5.mui.com/material-ui/react-popper/
 *
 */
export const UnitPicker = memo(
  ({
    onChange: onChangeFromParams,
    values,
    valueIds: valueIdsFromParams,
    showFullUnitNames = false,
    size,
    multiple = true,
    unitOptions,
    homeUnitToShowFloatingIcons,
    disableClickOutsideUnit = false,
    haveAllOption: _haveAllOption = false,
    reversed = false,
    className,
    dropdownClassName,
    label,
  }: {
    onChange: (selectedUnits: IUnit[]) => void; // Callback when selection changes
    values?: IUnit[]; // Selected units (alternative to valueIds)
    valueIds?: IUnit["id"][]; // IDs of selected units (alternative to values)
    showFullUnitNames?: boolean; // Whether to show full unit names in selection display or hierarchy (like market, facility, etc.)
    size?: SelectProps["size"]; // Size of the select component
    multiple?: boolean; // New prop
    unitOptions?: IUnit[] | IAppConfig["accessibleUnits"]; // Scope down the units to choose from
    homeUnitToShowFloatingIcons?: IUnit["id"]; // Unit ID to not show floating icons for, all the other units will show floating icons
    disableClickOutsideUnit?: boolean; // Disable click outside unit to select
    haveAllOption?: boolean; // Whether to show the "All" option
    reversed?: boolean; // Whether to reverse the direction of the unit picker
    className?: string; // Additional CSS class name
    dropdownClassName?: string; // Additional CSS class name for the dropdown component
    label?: string; // Label for the unit picker
  }) => {
    // Add search state
    const [searchQuery, setSearchQuery] = useState("");
    // Add open state
    const [open, setOpen] = useState(false);
    // Add ref for detecting clicks outside
    const selectRef = useRef<HTMLDivElement>(null);

    const WIDTH = "225px";

    // Handle click outside to close dropdown
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (selectRef.current && !selectRef.current.contains(event.target as Node) && open) {
          setOpen(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [open]);

    // Override MUI's default close-on-select behavior
    const handleMouseDown = useCallback((event: React.MouseEvent) => {
      // Prevent the dropdown from closing on item selection
      event.preventDefault();
      event.stopPropagation();
      const target = event.target as HTMLElement;

      // Only allow closing when clicking outside, not on menu items
      if (target.closest(".MuiBackdrop-root")) {
        setOpen(false);
      }
    }, []);

    // Get health systems data from app config
    const appConfig = useAppConfigQuery().data;
    const healthSystems = useMemo(() => appConfig?.healthSystems || [], [appConfig?.healthSystems]);
    const facilityById = useKeyBy(appConfig?.facilities, "id");
    const unitById = useKeyBy(appConfig?.accessibleUnits, "id");

    // Generate a flat list of all units from health systems, indexed by ID for quick lookup
    const allUnitsMap = useMemo(() => {
      const possibleUnitMap = new Map<string, IUnit>();

      // Extract units from the nested structure
      healthSystems.forEach((healthSystem) => {
        healthSystem.regions?.forEach((region) => {
          if (!region.archivedAt) {
            region.markets?.forEach((market) => {
              if (!market.archivedAt) {
                market.facilityIds?.forEach((facilityId) => {
                  const facility = facilityById[facilityId];
                  if (!facility || facility.archivedAt) return;

                  facility.unitIds?.forEach((unitId) => {
                    const unit = unitById[unitId];
                    if (!unit || unit.archivedAt) return;

                    possibleUnitMap.set(unit.id, unit as IUnit);
                  });
                });
              }
            });
          }
        });
      });

      let finalUnitMap = new Map<string, IUnit>();

      if (unitOptions) {
        unitOptions.forEach((unit) => {
          if (!unit.archivedAt) {
            const possibleUnit = possibleUnitMap.get(unit.id);
            if (possibleUnit) {
              finalUnitMap.set(unit.id, possibleUnit);
            }
          }
        });
      } else {
        finalUnitMap = possibleUnitMap;
      }

      return finalUnitMap;
    }, [healthSystems, unitOptions, facilityById, unitById]);

    // Normalize valueIds from either values prop or valueIds prop
    const computedValueIds = useDeepMemo(
      () => valueIdsFromParams || map(values, "id"),
      [valueIdsFromParams, values],
    );
    const [valueIds, setInternalValueIds] = useState<IUnit["id"][]>(computedValueIds);
    const propagateOnChange = useCallback(() => {
      if (!allUnitsMap) return;

      const idsFromParams = computedValueIds;
      const newIds = valueIds;
      if (!isEqual(idsFromParams, newIds)) {
        onChangeFromParams?.(newIds.map((id) => allUnitsMap.get(id)!));
      }
    }, [computedValueIds, valueIds, onChangeFromParams, allUnitsMap]);

    const onChange = useCallback(
      (selectedUnits: IUnit[]) => {
        setInternalValueIds(selectedUnits.map((unit) => unit.id));

        if (!multiple) setOpen(false);
      },
      [multiple],
    );

    // (Re)set initial valueIds
    useEffect(() => {
      setInternalValueIds(computedValueIds);
    }, [computedValueIds]);
    // Propagate changes when the dropdown is closed
    const shouldPropagateOnClose = useRef(false);
    useEffect(() => {
      if (!open && shouldPropagateOnClose.current) {
        shouldPropagateOnClose.current = false;
        propagateOnChange();
      }
      if (open) shouldPropagateOnClose.current = true;
    }, [open, computedValueIds.length, propagateOnChange]);

    // Create hierarchical node structure - we'll keep this part for data organization
    const hierarchyNodes = useMemo(() => {
      const nodes: IHierarchyNode[] = [];

      // Transform the nested structure into a flat list of nodes with parent references
      healthSystems.forEach((healthSystem) => {
        healthSystem.regions?.forEach((region) => {
          region.markets?.forEach((market) => {
            const marketUnits: IHierarchyNode[] = [];
            const marketFacilities: IHierarchyNode[] = [];

            market.facilityIds?.forEach((facilityId) => {
              const facility = facilityById[facilityId];
              if (!facility || facility.archivedAt) return;

              const facilityUnits: IHierarchyNode[] = [];

              facility.unitIds?.forEach((unitId) => {
                const unit = unitById[unitId];
                if (!unit || unit.archivedAt) return;

                // Only add unit if it's not archived and is in the allUnitsMap
                if (!unit.archivedAt && allUnitsMap.has(unit.id)) {
                  facilityUnits.push({
                    id: `unit_${unit.id}`,
                    name: unit.name,
                    parentId: `facility_${facility.id}`,
                    type: "unit",
                    level: 2,
                    unitId: unit.id,
                  });
                }
              });

              // Only add facility if it has units
              if (facilityUnits.length > 0) {
                const facilityNode: IHierarchyNode = {
                  id: `facility_${facility.id}`,
                  name: facility.name,
                  parentId: `market_${market.id}`,
                  type: "facility",
                  level: 1,
                };
                marketFacilities.push(facilityNode);
                marketUnits.push(...facilityUnits);
              }
            });

            // Only add market and its children if it has units
            if (marketUnits.length > 0) {
              const marketNode: IHierarchyNode = {
                id: `market_${market.id}`,
                name: market.name,
                type: "market",
                level: 0,
              };
              nodes.push(marketNode);
              nodes.push(...marketFacilities);
              nodes.push(...marketUnits);
            }
          });
        });
      });

      return nodes;
    }, [healthSystems, allUnitsMap, facilityById, unitById]);

    // Get node by ID for quick lookup
    const nodesByIdMap = useMemo(() => {
      return hierarchyNodes.reduce(
        (acc, node) => {
          acc[node.id] = node;
          return acc;
        },
        {} as Record<string, IHierarchyNode>,
      );
    }, [hierarchyNodes]);

    // Get child nodes for a given parent ID
    const getChildNodes = useCallback(
      (parentId: string) => {
        return hierarchyNodes.filter((node) => node.parentId === parentId);
      },
      [hierarchyNodes],
    );

    // Get unit IDs for a given parent node (recursively)
    const getUnitIdsForNode = useCallback(
      (nodeId: string): string[] => {
        const node = nodesByIdMap[nodeId];
        if (!node) return [];

        if (node.type === "unit" && node.unitId) {
          return [node.unitId];
        } else {
          // Get all children nodes
          const childNodes = getChildNodes(nodeId);
          // Recursively get unit IDs for all children
          return flatMap(childNodes, (child) => getUnitIdsForNode(child.id));
        }
      },
      [nodesByIdMap, getChildNodes],
    );

    // Create options array for the select component
    const options = useMemo(
      () =>
        hierarchyNodes
          .filter((node) => node.type === "unit")
          .map((node) => ({
            label: node.name,
            value: node.unitId!,
          })),
      [hierarchyNodes],
    );

    // Filter valueIds to only include available units
    const selectedValueIds = useMemo(
      () => intersection(valueIds, Array.from(allUnitsMap.keys())),
      [allUnitsMap, valueIds],
    );

    // Calculate which nodes are fully or partially selected
    const nodeSelectionState = useMemo(() => {
      const result: Record<string, INodeSelectionState> = {};

      hierarchyNodes.forEach((node) => {
        if (node.type === "unit") {
          const isSelected = node.unitId ? selectedValueIds.includes(node.unitId) : false;
          result[node.id] = {
            allSelected: isSelected,
            someSelected: isSelected,
            unitIds: node.unitId ? [node.unitId] : [],
          };
        } else {
          const unitIds = getUnitIdsForNode(node.id);
          const selectedUnitIds = intersection(unitIds, selectedValueIds);

          result[node.id] = {
            allSelected: selectedUnitIds.length === unitIds.length && unitIds.length > 0,
            someSelected: selectedUnitIds.length > 0,
            unitIds,
          };
        }
      });

      return result;
    }, [hierarchyNodes, selectedValueIds, getUnitIdsForNode]);

    // Create Fuse instance for fuzzy search
    const fuse = useMemo(() => {
      const searchableNodes = hierarchyNodes.map((node) => ({
        id: node.id,
        name: node.name,
        type: node.type,
        unitId: node.unitId,
        parentId: node.parentId,
      }));

      return new Fuse(searchableNodes, {
        keys: ["name"],
        threshold: 0.3,
        ignoreLocation: true,
      });
    }, [hierarchyNodes]);

    // Filter nodes based on search query
    const filteredNodes = useMemo(() => {
      if (!searchQuery.trim()) return hierarchyNodes;

      const results = fuse.search(searchQuery);
      const matchedNodeIds = new Set(results.map((result) => result.item.id));

      // Include parent nodes of matched nodes
      const relevantNodeIds = new Set(matchedNodeIds);
      results.forEach((result) => {
        let currentNode = nodesByIdMap[result.item.id];
        while (currentNode?.parentId) {
          relevantNodeIds.add(currentNode.parentId);
          currentNode = nodesByIdMap[currentNode.parentId];
        }
      });

      return hierarchyNodes.filter((node) => relevantNodeIds.has(node.id));
    }, [searchQuery, fuse, hierarchyNodes, nodesByIdMap]);

    // Highlight search matches in node names
    const highlightSearchMatch = useCallback(
      (text: string) => {
        if (!searchQuery.trim()) return text;

        const parts = text.split(new RegExp(`(${searchQuery})`, "gi"));
        return (
          <div>
            {parts.map((part, i) =>
              part.toLowerCase() === searchQuery.toLowerCase() ? (
                <span
                  key={i}
                  style={{ backgroundColor: "rgba(0, 0, 0, 0.1)", borderRadius: "2px" }}
                >
                  {part}
                </span>
              ) : (
                part
              ),
            )}
          </div>
        );
      },
      [searchQuery],
    );

    // Get markets
    const markets = useMemo(
      () => hierarchyNodes.filter((node) => node.type === "market"),
      [hierarchyNodes],
    );

    // Filter markets by search if search is active
    const filteredMarkets = searchQuery
      ? markets.filter(
          (market) =>
            filteredNodes.some((node) => node.id === market.id) ||
            getChildNodes(market.id).some(
              (facility) =>
                filteredNodes.some((node) => node.id === facility.id) ||
                getChildNodes(facility.id).some((unit) =>
                  filteredNodes.some((node) => node.id === unit.id),
                ),
            ),
        )
      : markets;

    // Check if all units belong to a single facility
    const singleMarketWithSingleFacility =
      filteredMarkets.length === 1 && getChildNodes(filteredMarkets[0]!.id).length === 1;

    // Helper function for getting floating icons
    const getFloatingIconForUnit = useCallback(
      (unitId: string | undefined, homeUnitId: string | undefined) => {
        if (!unitId || !homeUnitId || unitId === homeUnitId) {
          return null;
        }
        return <CompactFloatedTag unitId={homeUnitId} grayIfNotFloatedStatus />;
      },
      [],
    );

    // Handle individual unit selection from the dropdown
    const onUnitSelect = useCallback(
      (event: SelectChangeEvent<unknown>) => {
        // Prevent the default behavior of closing the dropdown
        event.preventDefault();
        event.stopPropagation();

        if (!multiple) {
          const newValue = event.target.value as string;
          const selectedUnit = allUnitsMap.get(newValue);
          onChange(selectedUnit ? [selectedUnit] : []);
          setOpen(false); // Only close for single select
          return;
        }

        const newSelectedUnitIds = event.target.value as string[];

        if (newSelectedUnitIds.includes("all")) {
          onChange([]);
          return;
        }

        const selectedUnits = newSelectedUnitIds
          .map((id) => allUnitsMap.get(id))
          .filter(Boolean) as IUnit[];

        onChange(selectedUnits);
        // Keep dropdown open for multiple select
      },
      [allUnitsMap, onChange, multiple],
    );

    // Handle node selection - for selecting facilities/markets
    const onNodeSelect = useCallback(
      (nodeId: string) => {
        const node = nodesByIdMap[nodeId];
        if (!node) return;

        if (disableClickOutsideUnit && node.type !== "unit") {
          return;
        }

        const { allSelected, unitIds } = nodeSelectionState[nodeId] || {
          allSelected: false,
          unitIds: [],
        };

        const updatedIdSelection = allSelected
          ? selectedValueIds.filter((unitId) => !unitIds.includes(unitId))
          : uniq([...selectedValueIds, ...unitIds]);

        let selectedUnits = updatedIdSelection
          .map((id) => allUnitsMap.get(id))
          .filter(Boolean) as IUnit[];

        if (selectedUnits.length === allUnitsMap.size) {
          selectedUnits = [];
        }

        onChange(selectedUnits);
      },
      [
        allUnitsMap,
        nodeSelectionState,
        nodesByIdMap,
        onChange,
        selectedValueIds,
        disableClickOutsideUnit,
      ],
    );

    // Handle unit item click
    const handleUnitClick = useCallback(
      (unitId: string) => {
        if (!multiple) {
          const selectedUnit = allUnitsMap.get(unitId);
          onChange(selectedUnit ? [selectedUnit] : []);
          setOpen(false); // Close for single select
          return;
        }

        // Toggle unit selection
        const isSelected = selectedValueIds.includes(unitId);
        const newSelectedIds = isSelected
          ? selectedValueIds.filter((id) => id !== unitId)
          : [...selectedValueIds, unitId];

        const selectedUnits = newSelectedIds
          .map((id) => allUnitsMap.get(id))
          .filter(Boolean) as IUnit[];

        onChange(selectedUnits);
        // Keep it open for multiple select
      },
      [allUnitsMap, onChange, multiple, selectedValueIds],
    );

    // Handle URL query params synchronization
    useHandleQueryParams({
      onChange,
      valueIds: computedValueIds,
      withQueryParams: false, // Set to true if you want to enable URL param syncing
      nodeSelectionState,
      hierarchyNodes,
      options,
      isLoading: false,
      allUnitsMap,
    });

    const computedLabel = label || (multiple ? "Select Units" : "Select Unit");

    // Render the complete component
    return (
      <FormControl
        className={`m7-unit-group-picker`}
        sx={{ width: "100% ", minWidth: WIDTH }}
        size={size}
        ref={selectRef}
        onMouseDown={handleMouseDown}
      >
        <InputLabel id="unit-selector-label" children={computedLabel} />
        <Select
          labelId="unit-selector-label"
          multiple={multiple}
          value={multiple ? selectedValueIds : selectedValueIds[0] || ""}
          onChange={onUnitSelect}
          input={<OutlinedInput label={computedLabel} />}
          renderValue={(selected) => (
            <RenderSelectedValues
              {...{
                selected,
                multiple,
                showFullUnitNames,
                allUnitsMap,
                getFloatingIconForUnit,
                homeUnitToShowFloatingIcons,
                className,
                hierarchyNodes,
                nodeSelectionState,
                getChildNodes,
                selectedValueIds,
              }}
            />
          )}
          id="unit-selector"
          className={`unit-picker-select ${className || ""}`}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={(event) => {
            // Don't close when clicking in the dropdown
            const target = event.target as HTMLElement;
            const withinMenu = target.closest(".within-menu");

            if (!withinMenu) {
              setOpen(false);
            }
          }}
          MenuProps={{
            className: `m7-unit-group-picker-menu ${reversed ? "reversed" : ""} unit-picker-dropdown ${dropdownClassName || ""}`,
            PaperProps: {
              sx: {
                minWidth: WIDTH + " !important",
                "& .MuiList-root": {
                  paddingTop: 0,
                  paddingBottom: 0,
                  minWidth: WIDTH + " !important",
                },
              },
              // Add onMouseDown to prevent dropdown from closing
              onMouseDown: (event: React.MouseEvent) => {
                event.stopPropagation();
              },
            },
            // Standard Material-UI dropdown settings
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "left",
            },
            transformOrigin: {
              vertical: "top",
              horizontal: "left",
            },
            // Force the dropdown to be modal
            autoFocus: false,
            disableAutoFocusItem: true,
            disablePortal: false,
            keepMounted: false,
          }}
        >
          {/* Search box */}
          <Box
            className="m7-search-container within-menu"
            sx={{
              width: WIDTH,
              maxWidth: WIDTH,
              minWidth: WIDTH,
              boxSizing: "border-box",
            }}
          >
            <TextField
              fullWidth
              size="small"
              placeholder="Search for units..."
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
            />
          </Box>

          {/* Render the hover-expanding dropdown */}
          <RenderExpandingDropdown
            singleMarketWithSingleFacility={singleMarketWithSingleFacility}
            filteredMarkets={filteredMarkets}
            getChildNodes={getChildNodes}
            nodeSelectionState={nodeSelectionState}
            selectedValueIds={selectedValueIds}
            handleUnitClick={handleUnitClick}
            onNodeSelect={onNodeSelect}
            highlightSearchMatch={highlightSearchMatch}
            getFloatingIconForUnit={getFloatingIconForUnit}
            homeUnitToShowFloatingIcons={homeUnitToShowFloatingIcons}
            searchQuery={searchQuery}
            filteredNodes={filteredNodes}
            multiple={multiple}
            reversed={reversed}
          />
        </Select>
      </FormControl>
    );
  },
);

// TODO: Those component were created to allow not having to re-render parent component leading to loosing\
//  sub-selections of region > market popover
// They use to be const = () within the component.
// Taking them out of the parent component fix our issue but forces us to downstream a lot of params
// and such. It's not a big issue given the unit dropdown is rendered once a page always. But could be good
// to rethink components API

const Item = memo(function memoizedUnitDropdownItem({
  entity,
  isSelected,
  isMultiple,
  allSelected,
  someSelected,
  handleUnitClick,
  onNodeSelect,
  highlightSearchMatch,
  getFloatingIconForUnit,
  homeUnitToShowFloatingIcons,
}: {
  entity: IHierarchyNode;
  isSelected: boolean;
  isMultiple: boolean;
  allSelected: boolean;
  someSelected: boolean;
  handleUnitClick: (unitId: IUnit["id"]) => void;
  onNodeSelect: (nodeId: string) => void;
  highlightSearchMatch: (name: string) => string | ReactNode;
  getFloatingIconForUnit: (
    unitId: IUnit["id"],
    homeUnitToShowFloatingIcons?: IUnit["id"],
  ) => ReactNode;
  homeUnitToShowFloatingIcons?: IUnit["id"];
}) {
  return (
    <div
      className={`m7-item within-menu ${isSelected ? "selected" : ""}`}
      onClick={(event) => {
        event.stopPropagation();
        event.preventDefault();
        entity.unitId
          ? handleUnitClick(entity.unitId)
          : isMultiple
            ? onNodeSelect(entity.id)
            : null;
      }}
    >
      {isMultiple && (
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected && !allSelected}
          trackingLabel="`select-${entity.type}`"
          size="small"
        />
      )}
      <Typography className="m7-item-name">{highlightSearchMatch(entity.name)}</Typography>
      {entity.unitId && getFloatingIconForUnit(entity.unitId, homeUnitToShowFloatingIcons)}
      {/*{entity.type !== "unit" && <ArrowRight className="m7-item-arrow" />}*/}
    </div>
  );
});

// Render the selected values as chips in the select input
const RenderSelectedValues = memo(function memoizedRenderSelectedValues({
  selected,
  multiple,
  showFullUnitNames,
  allUnitsMap,
  getFloatingIconForUnit,
  homeUnitToShowFloatingIcons,
  className,
  hierarchyNodes,
  nodeSelectionState,
  getChildNodes,
  selectedValueIds,
}: {
  selected: unknown;
  multiple: boolean;
  showFullUnitNames: boolean;
  allUnitsMap: Map<string, IUnit>;
  getFloatingIconForUnit: (
    unitId: IUnit["id"],
    homeUnitToShowFloatingIcons?: IUnit["id"],
  ) => ReactNode;
  homeUnitToShowFloatingIcons?: IUnit["id"];
  className?: string;
  hierarchyNodes: IHierarchyNode[];
  nodeSelectionState: Record<string, INodeSelectionState>;
  getChildNodes: (parentId: string) => IHierarchyNode[];
  selectedValueIds: IUnit["id"][];
}) {
  if (!multiple) {
    const unitId = selected as string;
    const unit = allUnitsMap.get(unitId);
    return unit ? (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        {unit.name}
        {getFloatingIconForUnit(unit.id, homeUnitToShowFloatingIcons)}
      </Box>
    ) : null;
  }

  const selectedArray = selected as string[];

  if (showFullUnitNames) {
    return (
      <Box
        className={className}
        sx={{
          display: "flex",
          gap: 0.5,
        }}
      >
        {selectedArray.map((unitId) => {
          const unit = allUnitsMap.get(unitId);
          return unit ? (
            <Chip
              key={unit.id}
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  {unit.name}
                  {getFloatingIconForUnit(unit.id, homeUnitToShowFloatingIcons)}
                </Box>
              }
              size="small"
            />
          ) : null;
        })}
      </Box>
    );
  }

  // Otherwise, group by market and facility and show counts
  const chips: { label: string; key: string }[] = [];

  // Find markets with fully selected facilities/units
  const marketsWithSelection = hierarchyNodes.filter(
    (node) => node.type === "market" && nodeSelectionState[node.id]?.someSelected,
  );

  // For each market, check selection status
  marketsWithSelection.forEach((market) => {
    const marketState = nodeSelectionState[market.id];

    if (marketState?.allSelected) {
      // If all units in market are selected, show the market
      chips.push({
        key: market.id,
        label: `${market.name} (${marketState.unitIds.length})`,
      });
    } else {
      // For partially selected markets, show facilities with selections
      const facilitiesWithSelection = getChildNodes(market.id).filter(
        (node) => nodeSelectionState[node.id]?.someSelected,
      );

      facilitiesWithSelection.forEach((facility) => {
        const facilityState = nodeSelectionState[facility.id];

        if (facilityState?.allSelected) {
          // If all units in facility are selected, show the facility
          chips.push({
            key: facility.id,
            label: `${facility.name} (${facilityState.unitIds.length})`,
          });
        } else {
          // For partially selected facilities, show unit count
          const selectedUnitCount = intersection(
            facilityState?.unitIds || [],
            selectedValueIds,
          ).length;
          if (selectedUnitCount > 0) {
            chips.push({
              key: facility.id,
              label: `${facility.name} (${selectedUnitCount})`,
            });
          }
        }
      });
    }
  });

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        marginRight: 2.5,
        alignItems: "flex-start",
      }}
      className={`m7-unit-group-picker-chips ${className || ""}`}
    >
      {chips.map((chip) => (
        <Chip
          key={chip.key}
          label={chip.label}
          size="small"
          className="m7-unit-group-picker-chip"
        />
      ))}
    </Box>
  );
});

const RenderSection = memo(function memoizedRenderSection({ name }: { name: string }) {
  return (
    <div className="m7-section-header">
      <Typography className="m7-item-name" fontWeight={500} fontSize={14} color={"text.secondary"}>
        <div>{name}</div>
      </Typography>
    </div>
  );
});

const RenderEntity = memo(function memoizedRenderEntity({
  entity,
  children,
  isMultiple,
  nodeSelectionState,
  selectedValueIds,
  handleUnitClick,
  onNodeSelect,
  highlightSearchMatch,
  getFloatingIconForUnit,
  homeUnitToShowFloatingIcons,
  reversed,
}: {
  entity: IHierarchyNode;
  children?: ReactNode;
  isMultiple: boolean;
  nodeSelectionState: Record<string, INodeSelectionState>;
  selectedValueIds: IUnit["id"][];
  handleUnitClick: (unitId: IUnit["id"]) => void;
  onNodeSelect: (nodeId: string) => void;
  highlightSearchMatch: (name: string) => string | ReactNode;
  getFloatingIconForUnit: (
    unitId: IUnit["id"],
    homeUnitToShowFloatingIcons?: IUnit["id"],
  ) => ReactNode;
  homeUnitToShowFloatingIcons?: IUnit["id"];
  reversed: boolean;
}) {
  const { someSelected, allSelected } = nodeSelectionState[entity.id] || {
    someSelected: false,
    allSelected: false,
  };
  const isSelected = entity.unitId ? selectedValueIds.includes(entity.unitId) : false;

  const item = (
    <Item
      entity={entity}
      isSelected={isSelected}
      isMultiple={isMultiple}
      allSelected={allSelected}
      someSelected={someSelected}
      handleUnitClick={handleUnitClick}
      onNodeSelect={onNodeSelect}
      highlightSearchMatch={highlightSearchMatch}
      getFloatingIconForUnit={getFloatingIconForUnit}
      homeUnitToShowFloatingIcons={homeUnitToShowFloatingIcons}
    />
  );
  return (
    <>
      {!children ? (
        item
      ) : (
        <Tooltip
          placement={reversed ? "left" : "right"}
          title={children}
          componentsProps={{
            popper: {
              className: "m7-unit-group-picker-menu",
              keepMounted: true,
            },
            tooltip: {
              sx: {
                backgroundColor: "transparent",
              },
            },
          }}
        >
          {/* div wrapper is required by Tooltip for ref */}
          <div>{item}</div>
        </Tooltip>
      )}
    </>
  );
});

const RenderEntityAndNodes = ({
  entities,
  searchQuery,
  filteredNodes,
  getChildNodes,
  multiple,
  nodeSelectionState,
  selectedValueIds,
  handleUnitClick,
  onNodeSelect,
  highlightSearchMatch,
  getFloatingIconForUnit,
  homeUnitToShowFloatingIcons,
  reversed,
}: {
  entities: IHierarchyNode[];
  searchQuery: string;
  filteredNodes: IHierarchyNode[];
  getChildNodes: (parentId: string) => IHierarchyNode[];
  multiple: boolean;
  nodeSelectionState: Record<string, INodeSelectionState>;
  selectedValueIds: IUnit["id"][];
  handleUnitClick: (unitId: IUnit["id"]) => void;
  onNodeSelect: (nodeId: string) => void;
  highlightSearchMatch: (name: string) => string | ReactNode;
  getFloatingIconForUnit: (
    unitId: IUnit["id"],
    homeUnitToShowFloatingIcons?: IUnit["id"],
  ) => ReactNode;
  homeUnitToShowFloatingIcons?: IUnit["id"];
  reversed: boolean;
}) => {
  return (
    <>
      {entities.map((entity) => {
        const filtered = searchQuery
          ? filteredNodes.some((node) => node.id === entity.id) ||
            getChildNodes(entity.id).some((unit) =>
              filteredNodes.some((node) => node.id === unit.id),
            )
          : true;

        if (!filtered) return null;

        const nodes = getChildNodes(entity.id);
        return (
          <div key={entity.id} className="m7-row">
            <RenderEntity
              entity={entity}
              isMultiple={multiple}
              nodeSelectionState={nodeSelectionState}
              selectedValueIds={selectedValueIds}
              handleUnitClick={handleUnitClick}
              onNodeSelect={onNodeSelect}
              highlightSearchMatch={highlightSearchMatch}
              getFloatingIconForUnit={getFloatingIconForUnit}
              homeUnitToShowFloatingIcons={homeUnitToShowFloatingIcons}
              reversed={reversed}
            >
              {entity.type !== "unit" && (
                <div className={`m7-container ${entity.type}`}>
                  <RenderSection name={`${sectionNames[entity.type]} (${nodes.length})`} />
                  <div className="m7-dropdown-container">
                    <RenderEntityAndNodes
                      reversed={reversed}
                      entities={nodes}
                      searchQuery={searchQuery}
                      filteredNodes={filteredNodes}
                      getChildNodes={getChildNodes}
                      multiple={multiple}
                      nodeSelectionState={nodeSelectionState}
                      selectedValueIds={selectedValueIds}
                      handleUnitClick={handleUnitClick}
                      onNodeSelect={onNodeSelect}
                      highlightSearchMatch={highlightSearchMatch}
                      getFloatingIconForUnit={getFloatingIconForUnit}
                      homeUnitToShowFloatingIcons={homeUnitToShowFloatingIcons}
                    />
                  </div>
                </div>
              )}
            </RenderEntity>
          </div>
        );
      })}
    </>
  );
};

// Render the hover-expanding dropdown
const RenderExpandingDropdown = memo(function memoizedRenderExpandingDropdown({
  singleMarketWithSingleFacility,
  filteredMarkets,
  getChildNodes,
  nodeSelectionState,
  selectedValueIds,
  handleUnitClick,
  onNodeSelect,
  highlightSearchMatch,
  getFloatingIconForUnit,
  homeUnitToShowFloatingIcons,
  searchQuery,
  filteredNodes,
  multiple,
  reversed,
}: {
  singleMarketWithSingleFacility: boolean;
  filteredMarkets: IHierarchyNode[];
  getChildNodes: (parentId: string) => IHierarchyNode[];
  nodeSelectionState: Record<string, INodeSelectionState>;
  selectedValueIds: IUnit["id"][];
  handleUnitClick: (unitId: IUnit["id"]) => void;
  onNodeSelect: (nodeId: string) => void;
  highlightSearchMatch: (name: string) => string | ReactNode;
  getFloatingIconForUnit: (
    unitId: IUnit["id"],
    homeUnitToShowFloatingIcons?: IUnit["id"],
  ) => ReactNode;
  homeUnitToShowFloatingIcons?: IUnit["id"];
  searchQuery: string;
  filteredNodes: IHierarchyNode[];
  multiple: boolean;
  reversed: boolean;
}) {
  // If there's only one market with one facility, skip the market level
  if (singleMarketWithSingleFacility) {
    const singleMarket = filteredMarkets[0];
    if (!singleMarket) return null;
    const singleFacility = getChildNodes(singleMarket.id)[0];
    if (!singleFacility) return null;
    const entities = getChildNodes(singleFacility.id);

    return (
      <Box className="m7-nested-dropdown within-menu">
        <RenderSection name={`${singleFacility.name} UNITS`} />
        <div className="m7-dropdown-container">
          <RenderEntityAndNodes
            reversed={reversed}
            nodeSelectionState={nodeSelectionState}
            selectedValueIds={selectedValueIds}
            handleUnitClick={handleUnitClick}
            onNodeSelect={onNodeSelect}
            highlightSearchMatch={highlightSearchMatch}
            getFloatingIconForUnit={getFloatingIconForUnit}
            homeUnitToShowFloatingIcons={homeUnitToShowFloatingIcons}
            entities={entities}
            searchQuery={searchQuery}
            filteredNodes={filteredNodes}
            getChildNodes={getChildNodes}
            multiple={multiple}
          />
        </div>
      </Box>
    );
  }

  return (
    <Box className="m7-nested-dropdown within-menu">
      <RenderSection name={"MARKETS"} />
      <div className="m7-dropdown-container">
        <RenderEntityAndNodes
          reversed={reversed}
          entities={filteredMarkets}
          searchQuery={searchQuery}
          filteredNodes={filteredNodes}
          getChildNodes={getChildNodes}
          multiple={multiple}
          nodeSelectionState={nodeSelectionState}
          selectedValueIds={selectedValueIds}
          handleUnitClick={handleUnitClick}
          onNodeSelect={onNodeSelect}
          highlightSearchMatch={highlightSearchMatch}
          getFloatingIconForUnit={getFloatingIconForUnit}
          homeUnitToShowFloatingIcons={homeUnitToShowFloatingIcons}
        />
      </div>
    </Box>
  );
});
